import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { unreadNotificationCount, unreadMessagesCount } from "@/lib/social";

const RealtimeContext = createContext({ unreadNotifs: 0, unreadMsgs: 0, refresh: () => {} });

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const channelsRef = useRef([]);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setUnreadNotifs(0);
      setUnreadMsgs(0);
      return;
    }
    try {
      const [n, m] = await Promise.all([
        unreadNotificationCount(user.id),
        unreadMessagesCount(user.id),
      ]);
      setUnreadNotifs(n);
      setUnreadMsgs(m);
    } catch {
      /* ignore */
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    // tear down old channels on user change
    channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
    channelsRef.current = [];

    if (!user?.id) return;

    const notifCh = supabase
      .channel(`rt-notifs-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => refresh()
      )
      .subscribe();

    const msgCh = supabase
      .channel(`rt-msgs-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "messages", filter: `recipient_id=eq.${user.id}` },
        () => refresh()
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages", filter: `sender_id=eq.${user.id}` },
        () => refresh()
      )
      .subscribe();

    channelsRef.current = [notifCh, msgCh];

    return () => {
      [notifCh, msgCh].forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current = [];
    };
  }, [user?.id, refresh]);

  return (
    <RealtimeContext.Provider value={{ unreadNotifs, unreadMsgs, refresh }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeContext);
